// src/components/maintenance/MaintenanceOperationsCreate.js
import React, { useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  useRefresh,
  SaveButton,
  Toolbar,
  Loading,
} from 'react-admin';
import { Box, Typography, Alert } from '@mui/material';
import { supabaseClient } from '../supabase';
import { 
  generateTeachersCSV,
  generateClassesCSV,
  generateStudentsParentsCSV
} from './utils/csvGenerator';

// Export these functions from csvGenerator.js
// (I'll show you the update below)

const CreateToolbar = props => (
  <Toolbar {...props}>
    <SaveButton label="Generate Rollback CSVs" alwaysEnable />
  </Toolbar>
);

const MaintenanceOperationsCreate = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [generating, setGenerating] = useState(false);

  const currentYear = new Date().getFullYear();
  const defaultAlumniClassName = `${currentYear} Alumni`;

  const handleSave = async (values) => {
    setGenerating(true);

    try {
      // Get current user email
      const { data: { user } } = await supabaseClient.auth.getUser();

      // Generate all three CSVs
      const [teachersCSV, classesCSV, studentsParentsCSV] = await Promise.all([
        generateTeachersCSV(),
        generateClassesCSV(),
        generateStudentsParentsCSV(),
      ]);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      // Upload all three CSVs separately
      const teachersPath = `teachers_rollback_${timestamp}.csv`;
      const classesPath = `classes_rollback_${timestamp}.csv`;
      const studentsPath = `students_parents_rollback_${timestamp}.csv`;

      const uploadPromises = [
        supabaseClient.storage
          .from('maintenance-files')
          .upload(teachersPath, new Blob([teachersCSV], { type: 'text/csv;charset=utf-8' }), {
            contentType: 'text/csv;charset=utf-8',
          }),
        supabaseClient.storage
          .from('maintenance-files')
          .upload(classesPath, new Blob([classesCSV], { type: 'text/csv;charset=utf-8' }), {
            contentType: 'text/csv;charset=utf-8',
          }),
        supabaseClient.storage
          .from('maintenance-files')
          .upload(studentsPath, new Blob([studentsParentsCSV], { type: 'text/csv;charset=utf-8' }), {
            contentType: 'text/csv;charset=utf-8',
          }),
      ];

      const uploadResults = await Promise.all(uploadPromises);

      // Check for upload errors
      const uploadError = uploadResults.find(result => result.error);
      if (uploadError) throw uploadError.error;

      // Create maintenance operation record
      // Store all three paths as JSON
      const rollbackPaths = {
        teachers: teachersPath,
        classes: classesPath,
        students_parents: studentsPath,
      };

      const { data: newRecord, error: createError } = await supabaseClient
        .from('maintenance_operations')
        .insert({
          created_by: user.email,
          rollback_file_path: JSON.stringify(rollbackPaths), // Store as JSON
          status: 'pending',
          alumni_class_name: values.alumni_class_name || defaultAlumniClassName,
        })
        .select()
        .single();

      if (createError) throw createError;

      notify('Rollback CSVs generated successfully. You can now download them.', {
        type: 'success'
      });

      // Refresh the data provider cache
      refresh();

      // Small delay to ensure cache is updated
      setTimeout(() => {
        redirect('edit', 'maintenance_operations', newRecord.id);
      }, 100);

    } catch (error) {
      console.error('Error creating maintenance job:', error);
      notify(`Error: ${error.message}`, { type: 'error' });
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Loading />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Generating rollback CSVs from current database...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This includes teachers, classes, and students/parents data...
        </Typography>
      </Box>
    );
  }

  return (
    <Create {...props} title="Create Maintenance Job">
      <SimpleForm
        onSubmit={handleSave}
        toolbar={<CreateToolbar />}
        defaultValues={{
          alumni_class_name: defaultAlumniClassName,
        }}
      >
        <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
          <Typography variant="body2">
            This will create a snapshot of the current database (teachers, classes, students, and parents)
            as 3 separate CSV files. You can then download them as a ZIP, make your changes, and upload
            the edited files.
          </Typography>
        </Alert>

        <TextInput
          source="alumni_class_name"
          label="Alumni Class Name"
          helperText="Name for the alumni class (e.g., '2025 Alumni')"
          fullWidth
        />
      </SimpleForm>
    </Create>
  );
};

export default MaintenanceOperationsCreate;